// src/pages/applications/LearningApp.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../../components/LocalizedLink";

const ASSET = (p) => `${process.env.PUBLIC_URL || ""}${p}`;
const IMG = (f) => ASSET(`/learning/${f}`);

export default function LearningApp() {
  const { t } = useTranslation();

  const heroBullets =
    t("applications.detail.learning.hero.bullets", { returnObjects: true }) || [];

  const features = [
    "content",
    "enrollment",
    "progress",
    "assessments",
    "messaging",
    "analytics",
    "permissions",
    "branding",
    "integrations",
  ];

  const screens = [
    ["screenshot-dashboard.svg", "applications.detail.learning.screens.captions.dashboard"],
    ["screenshot-catalog.svg", "applications.detail.learning.screens.captions.catalog"],
    ["screenshot-lesson.svg", "applications.detail.learning.screens.captions.lesson"],
    ["screenshot-analytics.svg", "applications.detail.learning.screens.captions.analytics"],
  ];

  const plans = {
    starter: t("applications.detail.learning.pricing.items.starter", { returnObjects: true }),
    standard: t("applications.detail.learning.pricing.items.standard", { returnObjects: true }),
    plus: t("applications.detail.learning.pricing.items.plus", { returnObjects: true }),
  };

  return (
    <main className="bg-white text-slate-900">
      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-100">
              {t("applications.detail.learning.hero.badge_left")}
              <span className="inline-block size-1 rounded-full bg-indigo-300" />
              {t("applications.detail.learning.hero.badge_right")}
            </span>

            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">
              {t("applications.detail.learning.hero.title")}
            </h1>
            <p className="mt-3 text-slate-600">
              {t("applications.detail.learning.hero.desc")}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={IMG("cover.svg")}
                className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium hover:bg-indigo-500"
              >
                {t("applications.detail.learning.hero.view_demo")}
              </a>
              <LocalizedLink
                to="/contact"
                className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
              >
                {t("applications.detail.learning.hero.start")}
              </LocalizedLink>
            </div>

            <ul className="mt-6 grid grid-cols-2 gap-2 text-sm text-slate-600">
              {heroBullets.map((b, i) => (
                <li key={i}>• {b}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <img
              src={IMG("cover.svg")}
              alt={t("applications.detail.learning.hero.mock_alt")}
              className="w-full rounded-xl object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.learning.features.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.learning.features.sub")}
          </p>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((k) => (
              <FeatureCard
                key={k}
                icon={IMG(`mini-${k}.svg`)}
                title={t(`applications.detail.learning.features.items.${k}.title`)}
                desc={t(`applications.detail.learning.features.items.${k}.desc`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Screens */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.learning.screens.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.learning.screens.sub")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {screens.map(([src, capKey]) => (
              <figure key={src} className="rounded-2xl border border-slate-200 bg-white p-3">
                <img
                  src={IMG(src)}
                  alt={t(capKey)}
                  className="w-full rounded-xl object-cover"
                  loading="lazy"
                />
                <figcaption className="px-2 pb-2 pt-3 text-sm text-slate-600">
                  {t(capKey)}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* How it adapts */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.learning.adapt.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.learning.adapt.sub")}
          </p>

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <InfoCard
              icon={IMG("mini-modules.svg")}
              title={t("applications.detail.learning.adapt.cards.modules.title")}
              bullets={t(
                "applications.detail.learning.adapt.cards.modules.bullets",
                { returnObjects: true }
              )}
            />
            <InfoCard
              icon={IMG("mini-usecases.svg")}
              title={t("applications.detail.learning.adapt.cards.usecases.title")}
              bullets={t(
                "applications.detail.learning.adapt.cards.usecases.bullets",
                { returnObjects: true }
              )}
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.learning.pricing.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.learning.pricing.sub")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <PriceCard
              name={plans.starter.name}
              price={plans.starter.price}
              bullets={plans.starter.bullets || []}
            />
            <PriceCard
              featured
              name={plans.standard.name}
              price={plans.standard.price}
              bullets={plans.standard.bullets || []}
            />
            <PriceCard
              name={plans.plus.name}
              price={plans.plus.price}
              bullets={plans.plus.bullets || []}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-indigo-600 text-white">
        <div className="mx-auto max-w-7xl px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.learning.cta.title")}
          </h2>
          <p className="mt-2 text-indigo-100">
            {t("applications.detail.learning.cta.sub")}
          </p>
          <div className="mt-6">
            <LocalizedLink
              to="/contact"
              className="inline-block rounded-xl bg-white px-6 py-3 font-medium text-indigo-700 hover:bg-indigo-50"
            >
              {t("applications.cta.contact")}
            </LocalizedLink>
          </div>
        </div>
      </section>
    </main>
  );
}

/* --- Presentational bits --- */

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6 bg-white">
      <img src={icon} alt="" className="h-8 w-8" loading="lazy" />
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{desc}</p>
    </div>
  );
}

function InfoCard({ icon, title, bullets = [] }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6 bg-white">
      <img src={icon} alt="" className="h-7 w-7" loading="lazy" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

function PriceCard({ name, price, bullets, featured }) {
  return (
    <div
      className={
        "rounded-2xl border p-6 " +
        (featured ? "border-indigo-200 bg-indigo-50 shadow-md" : "border-slate-200 bg-white")
      }
    >
      <div className="text-sm uppercase tracking-wide text-slate-500">{name}</div>
      <div className="mt-2 text-3xl font-bold">{price}</div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {(bullets || []).map((b, i) => (
          <li key={i}>• {b}</li>
        ))}
      </ul>
    </div>
  );
}
